const RECOVERY_PREFIX = "pp:deploy-chunk-recovery:";
export const DEPLOY_CHUNK_RECOVERY_STORAGE_PREFIX = RECOVERY_PREFIX;
export const DEPLOY_CHUNK_RECOVERY_OVERRIDE_KEY = "__PP_ENABLE_DEPLOY_CHUNK_RECOVERY__";

export type DeployRecoveryClassification = "chunk-load" | "html-mime" | "missing-default-export";

export type DeployRecoveryEvent = {
  entryScriptUrl: string;
  requestedLazyUrl?: string;
  releaseIdentifier: string;
  classification: DeployRecoveryClassification;
  reloadCount: number;
  serviceWorkerControllerPresent: boolean;
};

export type DeployRecoveryReporter = (event: DeployRecoveryEvent) => void;

export class StaleDeployChunkError extends Error {
  readonly isStaleDeployChunkError = true;
  readonly classification: DeployRecoveryClassification;
  readonly moduleLabel: string;
  readonly requestedLazyUrl?: string;

  // The Product tsconfig lib predates ES2022's ErrorOptions.
  readonly cause?: unknown;

  constructor(
    moduleLabel: string,
    cause?: unknown,
    classification: DeployRecoveryClassification = "missing-default-export",
  ) {
    const detail = errorMessage(cause);
    super(`Failed to load lazy module "${moduleLabel}"${detail ? `: ${detail}` : ""}`);
    this.cause = cause;
    this.classification = classification;
    this.moduleLabel = moduleLabel;
    this.requestedLazyUrl = extractRequestedLazyUrl(cause);
    this.name = "StaleDeployChunkError";
    if (cause instanceof Error && cause.stack) {
      this.stack = `${this.stack ?? this.message}\nCaused by: ${cause.stack}`;
    }
  }
}

const CHUNK_LOAD_FAILURE_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|not a valid JavaScript MIME type/i;
const DYNAMIC_IMPORT_FAILURE_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

function errorMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === "string") return reason;
  if (reason && typeof reason === "object" && "message" in reason) {
    return String((reason as { message: unknown }).message);
  }
  return "";
}

function errorStack(reason: unknown): string {
  if (reason instanceof Error) return reason.stack ?? "";
  return "";
}

function extractRequestedLazyUrl(reason: unknown): string | undefined {
  const haystack = `${errorMessage(reason)}\n${errorStack(reason)}`;
  const match = haystack.match(/(?:https?:\/\/[^\s'"\])]+|\/assets\/[^\s'"\])]+\.js)/i);
  return match?.[0];
}

function isTaggedStaleDeployChunkError(reason: unknown): reason is StaleDeployChunkError {
  return reason instanceof StaleDeployChunkError
    || (typeof reason === "object"
      && reason !== null
      && (reason as { isStaleDeployChunkError?: unknown }).isStaleDeployChunkError === true);
}

function mentionsStaleDeployMimeFailure(haystack: string): boolean {
  if (/not a valid JavaScript MIME type/i.test(haystack)) return true;
  if (/mime type/i.test(haystack) && /text\/html/i.test(haystack)) return true;
  if (/Expected a JavaScript.*module script/i.test(haystack) && /text\/html/i.test(haystack)) {
    return true;
  }
  return false;
}

function deployChunkRecoveryTestHarnessEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const flags = window as Window & { [DEPLOY_CHUNK_RECOVERY_OVERRIDE_KEY]?: boolean };
  return flags[DEPLOY_CHUNK_RECOVERY_OVERRIDE_KEY] === true;
}

function deployChunkRecoveryEnabled(): boolean {
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return deployChunkRecoveryTestHarnessEnabled();
  }
  return true;
}

function isChunkLoadFailure(reason: unknown): boolean {
  return CHUNK_LOAD_FAILURE_RE.test(errorMessage(reason))
    || mentionsStaleDeployMimeFailure(errorMessage(reason));
}

function isRetriableDynamicImportFailure(reason: unknown): boolean {
  const message = errorMessage(reason);
  if (!DYNAMIC_IMPORT_FAILURE_RE.test(message)) return false;
  return !mentionsStaleDeployMimeFailure(`${message}\n${errorStack(reason)}`);
}

function throwClassifiedLoadFailure(moduleLabel: string, reason: unknown): never {
  if (isTaggedStaleDeployChunkError(reason)) throw reason;
  // Only genuine chunk-load failures are stale-deploy candidates. A throw
  // during module evaluation is an app bug and must reach the boundary as
  // itself rather than being laundered into a reload.
  if (isChunkLoadFailure(reason)) {
    const classification = mentionsStaleDeployMimeFailure(`${errorMessage(reason)}\n${errorStack(reason)}`)
      ? "html-mime"
      : "chunk-load";
    throw new StaleDeployChunkError(moduleLabel, reason, classification);
  }
  throw reason;
}

export function lazyImportForRecovery<T>(
  load: () => Promise<T>,
  moduleLabel = "unknown lazy module",
): () => Promise<T> {
  const loadWithDefault = async (): Promise<T> => {
    const module = await load();
    const defaultExport = module && typeof module === "object" && "default" in module
      ? (module as { default?: unknown }).default
      : undefined;
    if (defaultExport === undefined) {
      throw new StaleDeployChunkError(moduleLabel);
    }
    return module;
  };

  return async () => {
    try {
      return await loadWithDefault();
    } catch (reason) {
      // A dynamic-import rejection is often a transient network change, and
      // the caller may cache this promise. HTML/MIME mismatches are stale
      // deploys and are not retried. One more attempt; the second failure is
      // classified the same way a first failure used to be.
      if (isTaggedStaleDeployChunkError(reason) || !isRetriableDynamicImportFailure(reason)) {
        throwClassifiedLoadFailure(moduleLabel, reason);
      }
    }

    try {
      return await loadWithDefault();
    } catch (reason) {
      throwClassifiedLoadFailure(moduleLabel, reason);
    }
  };
}

export function currentEntryScriptSrc(): string | null {
  if (typeof document === "undefined") return null;
  const script = document.querySelector('script[type="module"][src*="/assets/"]');
  return script?.getAttribute("src") ?? null;
}

function recoveryStorageKey(): string {
  return `${RECOVERY_PREFIX}${currentEntryScriptSrc() ?? "unknown"}`;
}

export function isStaleDeployChunkFailure(reason: unknown): boolean {
  if (isTaggedStaleDeployChunkError(reason)) return true;

  const message = errorMessage(reason);
  const stack = errorStack(reason);
  const haystack = `${message}\n${stack}`;

  if (mentionsStaleDeployMimeFailure(haystack)) return true;

  if (DYNAMIC_IMPORT_FAILURE_RE.test(haystack) && /\/assets\/[^?\s'"]+\.js/i.test(message)) {
    return true;
  }

  if (deployChunkRecoveryTestHarnessEnabled()) {
    if (DYNAMIC_IMPORT_FAILURE_RE.test(haystack) && /\/assets\/[^?\s'"]+\.js|\/src\/next\/app\//i.test(message)) {
      return true;
    }
  }

  return false;
}

function isStaleDeployScriptElement(event: Event): boolean {
  const target = event.target;
  if (!(target instanceof HTMLScriptElement)) return false;
  const src = target.getAttribute("src") ?? "";
  if (!src.includes("/assets/")) return false;
  const errorEvent = event as ErrorEvent;
  return mentionsStaleDeployMimeFailure(`${errorEvent.message ?? ""}\n${errorMessage(errorEvent.error)}`);
}

function reloadCount(): number {
  try {
    return sessionStorage.getItem(recoveryStorageKey()) === "1" ? 1 : 0;
  } catch {
    return 0;
  }
}

function serviceWorkerControllerPresent(): boolean {
  try {
    return Boolean(navigator.serviceWorker?.controller);
  } catch {
    return false;
  }
}

function reportRecovery(
  report: DeployRecoveryReporter | undefined,
  reason: unknown,
  releaseIdentifier: string | undefined,
  reloadCountBeforeRecovery: number,
): void {
  if (!report) return;
  const tagged = isTaggedStaleDeployChunkError(reason) ? reason : undefined;
  report({
    entryScriptUrl: currentEntryScriptSrc() ?? "unknown",
    requestedLazyUrl: tagged?.requestedLazyUrl ?? extractRequestedLazyUrl(reason),
    releaseIdentifier: releaseIdentifier?.trim() || "unknown",
    classification: tagged?.classification ?? "chunk-load",
    reloadCount: reloadCountBeforeRecovery,
    serviceWorkerControllerPresent: serviceWorkerControllerPresent(),
  });
}

export function reloadForStaleDeployChunk(): boolean {
  if (typeof window === "undefined" || !deployChunkRecoveryEnabled()) return false;

  const key = recoveryStorageKey();
  try {
    if (sessionStorage.getItem(key) === "1") return false;
    sessionStorage.setItem(key, "1");
  } catch {
    return false;
  }

  window.location.reload();
  return true;
}

export function installDeployChunkRecovery(options: {
  report?: DeployRecoveryReporter;
  releaseIdentifier?: string;
} = {}): void {
  if (typeof window === "undefined" || !deployChunkRecoveryEnabled()) return;

  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    const reloadCountBeforeRecovery = reloadCount();
    reloadForStaleDeployChunk();
    reportRecovery(options.report, undefined, options.releaseIdentifier, reloadCountBeforeRecovery);
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (!isStaleDeployChunkFailure(event.reason)) return;
    event.preventDefault();
    const reloadCountBeforeRecovery = reloadCount();
    reloadForStaleDeployChunk();
    reportRecovery(options.report, event.reason, options.releaseIdentifier, reloadCountBeforeRecovery);
  });

  window.addEventListener("error", (event) => {
    if (isStaleDeployScriptElement(event)) {
      event.preventDefault();
      const reloadCountBeforeRecovery = reloadCount();
      reloadForStaleDeployChunk();
      reportRecovery(options.report, event.error ?? event.message, options.releaseIdentifier, reloadCountBeforeRecovery);
      return;
    }
    const candidate = event.error ?? event.message;
    if (!isStaleDeployChunkFailure(candidate)) return;
    event.preventDefault();
    const reloadCountBeforeRecovery = reloadCount();
    reloadForStaleDeployChunk();
    reportRecovery(options.report, candidate, options.releaseIdentifier, reloadCountBeforeRecovery);
  });
}
